import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination
} from '@mui/material';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';
import toast from 'react-hot-toast';
import { getAllTypes, createType,  updateType,  deleteType} from '../apis/typesApi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';

const Type = () => {
   const {id}  = useParams()
  const { role } = useAuth();
  const isVendor = role === 'vendor';

  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', image: null });
  const [editCategory, setEditCategory] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const navigate = useNavigate()

  useEffect(() => {
    fetchCategories(id);
  }, [id]);

  const fetchCategories = async (id) => {
    try {
      const data = await getAllTypes();
      setCategories(data.filter(c=>c.subcategory_id===parseInt(id)));
      setFiltered(data.filter(c=>c.subcategory_id===parseInt(id)));
    } catch (err) {
      toast.error('Failed to fetch categories');
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    setFiltered(categories.filter(c => c.name.toLowerCase().includes(term)));
  };

  const handleImageChange = (e) => {
    setNewCategory({ ...newCategory, image: e.target.files[0] });
  };

  const handleCreate = async () => {
    if (isVendor) {
      toast.error('Vendors are not permitted to create types');
      return;
    }
    const formData = new FormData();
    formData.append('subcategory_id', id);
    formData.append('name', newCategory.name);
    if (newCategory.image) formData.append('image', newCategory.image);

    try {
      await createType(formData);
      toast.success('Category created');
      setOpenAdd(false);
      setNewCategory({ name: '', image: null });
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.error);
    }
  };

  const handleUpdate = async () => {
    if (isVendor) {
      toast.error('Vendors are not permitted to edit types');
      return;
    }
    const formData = new FormData();
    formData.append('name', editCategory.name);
    if (editCategory.image instanceof File) formData.append('image', editCategory.image);

    try {
      await updateType(editCategory.id, formData);
      toast.success('Updated');
      setOpenEdit(false);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.error);
    }
  };

  const handleDelete = async (id) => {
    if (isVendor) {
      toast.error('Vendors are not permitted to delete types');
      return;
    }
    try {
      await deleteType(id);
      toast.success('Deleted');
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.error);
    }
  };

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" my={2}>
        <Typography variant="h5" fontWeight="bold">Types</Typography>
        <TextField label="Search" value={searchTerm} onChange={handleSearch} />
        {!isVendor && <Button variant="contained" startIcon={<MdAdd />} onClick={() => setOpenAdd(true)}>Add</Button>}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Image</TableCell>
              {!isVendor && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((cat, idx) => (
              <TableRow key={cat.id}>
                <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                <TableCell><Link to={`${cat.id}`}>{cat.name}</Link></TableCell>
                <TableCell>
                  {cat.image && <img src={`https://skishore.in/api/public/${cat.image}`} alt={cat.name} width="60" />}
                </TableCell>
                {!isVendor && (
                  <TableCell>
                    <Button onClick={() => { setEditCategory(cat); setOpenEdit(true); }}><MdEdit /></Button>
                    <Button color="error" onClick={() => handleDelete(cat.id)}><MdDelete /></Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
        />
      </TableContainer>

      {/* Add Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="normal"
            value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} />
          <input type="file" onChange={handleImageChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal"
            value={editCategory?.name || ''}
            onChange={(e) => setEditCategory({ ...editCategory, name: e.target.value })} />
          <input type="file" onChange={(e) => setEditCategory({ ...editCategory, image: e.target.files[0] })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Type;
